/**
 * The source's `authFetch`, over the platform's session.
 *
 * APR's screens call `authFetch('/api/frameworks', …)` and the service layer attaches a
 * bearer token from localStorage. The platform authenticates with an HTTP-only session
 * cookie and mounts APR's API under `/api/apr`, so this does two things and nothing else:
 *
 *   * rewrites `/api/<x>` to `/api/apr/<x>`, so screen code keeps the source's paths
 *   * sends the session cookie instead of a bearer token
 *
 * Both are on §1.2's allowed list (API path prefix, auth integration). Screens are not
 * touched, which is the point — a ported screen that says `/api/frameworks` is easier to
 * diff against the source than one rewritten to say `/api/apr/frameworks`.
 */

import aprAxios from './aprAxios';

/** `/api/frameworks/x` -> `/api/apr/frameworks/x`. Absolute URLs pass through. */
function toPlatformUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/apr/')) return url;
  if (url.startsWith('/api/')) return `/api/apr/${url.slice('/api/'.length)}`;
  return url;
}

export const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  // A FormData body MUST NOT carry an explicit Content-Type. Only the browser knows the
  // multipart boundary, and it only sets the header when we leave it alone. Sending
  // `application/json` alongside FormData makes the server parse no files at all — the
  // upload returns without error and nothing arrives, which is exactly how the Knowledge
  // Library upload failed. axios (which the source uses) applies the same rule
  // internally, so this keeps the ported screens behaving as they do there.
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: HeadersInit = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers || {}),
  };
  return fetch(toPlatformUrl(url), {
    ...options,
    headers,
    // The platform's session cookie replaces the source's bearer token.
    credentials: 'include',
  });
};

/**
 * The source's default-exported `apiService`. Only the verbs its ported screens use are
 * provided; anything else would be a guess at an interface nothing calls.
 */

// ─── License Intelligence ────────────────────────────────────────────────────
//
// Ported from the source's `apiService`. The METHOD BODIES are the source's — same
// URLs, same params, same post-processing, same filename fallbacks on the downloads.
// Only the transport changed: axios becomes `authFetch`, so the platform's session
// cookie is used instead of a bearer token. `response.data` and `response.headers` are
// preserved as shapes so the bodies did not have to be rewritten around them.

/** axios-shaped GET: `{ data }`, with optional query params. */
async function jsonGet(url: string, params?: Record<string, unknown>) {
  const qs = params
    ? '?' +
      new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => [k, String(v)]),
      ).toString()
    : '';
  const res = await authFetch(url + qs);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return { data: await res.json() };
}

/** axios-shaped POST/PATCH/DELETE: `{ data }`. */
async function jsonSend(method: string, url: string, body?: unknown) {
  const res = await authFetch(url, {
    method,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
  return { data: await res.json().catch(() => null) };
}

/** axios-shaped blob GET: `{ data, headers }` — the download methods read both. */
async function blobGet(url: string, params?: Record<string, unknown>) {
  const qs = params
    ? '?' +
      new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => [k, String(v)]),
      ).toString()
    : '';
  const res = await authFetch(url + qs);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return {
    data: await res.blob(),
    headers: { 'content-disposition': res.headers.get('content-disposition') || '' },
  };
}

const apiService = {
  /**
   * The source's methods are written against an axios instance held as `this.api`.
   * Pointing that name at the axios-shaped client lets those bodies port verbatim
   * rather than being rewritten call-by-call. See ./aprAxios.ts.
   */
  api: aprAxios,

  get: (url: string) => authFetch(url).then((r) => r.json()),

  // The scoring-threshold verbs, with the source's exact snake_case <-> camelCase
  // mapping (services/api.ts:785). The field names are the contract with the API and
  // with ScoringModel; changing either side silently breaks the disposition bands.
  async getScoringThresholds() {
    const data = await authFetch('/api/scoring-thresholds').then((r) => r.json());
    return (data as any[]).map((threshold: any) => ({
      id: threshold.id,
      name: threshold.decision_name,
      minScore: threshold.min_score,
      maxScore: threshold.max_score,
      color: threshold.color,
      description: threshold.description,
      displayOrder: threshold.display_order,
    }));
  },

  async createScoringThreshold(thresholdData: any) {
    const payload = {
      decision_name: thresholdData.name,
      min_score: thresholdData.minScore,
      max_score: thresholdData.maxScore,
      color: thresholdData.color,
      description: thresholdData.description,
    };
    const threshold: any = await authFetch('/api/scoring-thresholds', {
      method: 'POST',
      body: JSON.stringify(payload),
    }).then((r) => r.json());
    return {
      id: threshold.id,
      name: threshold.decision_name,
      minScore: threshold.min_score,
      maxScore: threshold.max_score,
      color: threshold.color,
      description: threshold.description,
      displayOrder: threshold.display_order,
    };
  },

  async updateScoringThreshold(id: string, thresholdData: any) {
    const payload = {
      decision_name: thresholdData.name,
      min_score: thresholdData.minScore,
      max_score: thresholdData.maxScore,
      color: thresholdData.color,
      description: thresholdData.description,
    };
    return authFetch(`/api/scoring-thresholds/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }).then((r) => r.json());
  },

  /**
   * The source's `getApplications` (services/api.ts:270). Knowledge Library uses it to
   * scope a document to an application. The platform's /api/apr/applications already
   * returns the inventory, so only the transform is reproduced — and only the fields
   * this screen reads, because inventing the rest would be guessing at a shape nothing
   * consumes.
   */
  // Replaced a hand-written 3-field version (id, name, applicationIdCode) with the
  // SOURCE's method verbatim. That reduction was a live defect, not a simplification:
  // APRContext's calculateDashboardStats does `app.costs.forEach(...)`, and `costs` was
  // one of the ~20 fields the short version dropped — every APR screen using the store
  // crashed on render. It also dropped `licenseNegotiation`, which License Intelligence
  // reads. Ported whole, so the shape matches what every consumer expects.
  async getApplications(customerId?: string, modelId?: string) {
    const params = new URLSearchParams();
    if (customerId) params.append('customer_id', customerId);
    if (modelId) params.append('model_id', modelId);
    params.append('_t', Date.now().toString());
    
    console.log('🔄 API Service: Calling /applications endpoint...');
    const response = await this.api.get(`/applications?${params.toString()}`);
    console.log('📥 API Service: Raw applications response:', response.data);
    // THE ONE DEVIATION in this method: the source's endpoint returns a bare array, the
    // platform's returns `{ applications: [...] }` because its own Master Data grid was
    // built against that envelope. Unwrapping here rather than changing the endpoint
    // keeps the platform's verified Master Data screens untouched; the array below is
    // the same rows either way. Accepts both shapes so it survives either endpoint.
    const rows = Array.isArray(response.data)
      ? response.data
      : (response.data?.applications ?? []);
    // Transform snake_case API response to camelCase for TypeScript interfaces
    const transformedApps = rows.map((app: any) => ({
      id: app.id,
      name: app.name,
      description: app.description,
      customerId: app.customer_id,
      aprModelId: app.apr_model_id,
      costs: [], // Costs will be loaded separately if needed
      responses: [], // Responses will be loaded separately if needed
      recommendation: app.recommendation,
      overallScore: app.overall_score,
      // Application Master Data fields
      applicationIdCode: app.application_id_code,
      // Lifecycle progress — derived server-side per stage, never stored as flags.
      qaAnsweredCount: app.qa_answered_count,
      qaTotalQuestions: app.qa_total_questions,
      hasInitialDisposition: app.has_initial_disposition,
      // Kept apart because they carry different authority: a Q&A-driven disposition is
      // the framework's own answer, an AI one is advisory input the client may ignore.
      manualInitialDisposition: app.manual_initial_disposition,
      aiInitialDisposition: app.ai_initial_disposition,
      hasAiAnalysis: app.has_ai_analysis,
      hasFinalDisposition: app.has_final_disposition,
      hasIntegrationGroup: app.has_integration_group,
      hasRoadmap: app.has_roadmap,
      deploymentStatus: app.deployment_status,
      itHardwareInfrastructure: app.it_hardware_infrastructure,
      dateOfIntroduction: app.date_of_introduction,
      contractEndDate: app.contract_end_date,
      applicationOwner: app.application_owner,
      appOwnerEmail: app.app_owner_email,
      bpoEmail: app.bpo_email,
      securityOwnerEmail: app.security_owner_email,
      executiveOwnerEmail: app.executive_owner_email,
      technicalOwnerCustodian: app.technical_owner_custodian,
      toEmail: app.to_email,
      businessFunctionDomain: app.business_function_domain,
      applicationType: app.application_type,
      technologyStack: app.technology_stack,
      currentOperating: app.current_operating,
      userCount: app.user_count,
      businessCriticality: app.business_criticality,
      technicalHealthStatus: app.technical_health_status,
      integrationDependencies: app.integration_dependencies,
      vendorSupplier: app.vendor_supplier,
      parentApplicationId: app.parent_application_id,
      applicationDescription: app.application_description,
      internalNumber: app.internal_number,
      l1BusinessFunctionalArea: app.l1_business_functional_area,
      l2CapabilityFunction: app.l2_capability_function,
      excludedFromApr: app.excluded_from_apr === true || app.excluded_from_apr === 't',
      licenseNegotiation: app.license_negotiation === true || app.license_negotiation === 't',
      parentApplicationName: app.parent_application_name,
      parentApplicationCode: app.parent_application_code,
      isParent: app.is_parent,
      // Audit fields
      createdBy: app.created_by,
      createdByName: app.created_by_name,
      createdAt: app.created_at,
      updatedBy: app.updated_by,
      updatedByName: app.updated_by_name,
      updatedAt: app.updated_at
    }));
    console.log('🔄 API Service: Transformed applications:', transformedApps);
    return transformedApps;
  },

  async getLicensingEligible() {
    const response = await jsonGet('/api/licensing/eligible');
    return response.data;
  },

  async getLicensingInstructions() {
    const response = await jsonGet('/api/licensing/instructions');
    return response.data;
  },

  async createLicensingInstruction(payload: {
    name: string;
    description?: string;
    vendor_family?: string | null;
    body?: string;
    config_schema?: any;
    config?: any;
    application_ids?: string[];
  }) {
    const response = await jsonSend('POST', '/api/licensing/instructions', payload);
    return response.data;
  },

  async updateLicensingInstruction(
    id: string,
    payload: {
      name?: string;
      description?: string;
      vendor_family?: string | null;
      body?: string;
      config?: any;
      application_ids?: string[];
    },
  ) {
    const response = await jsonSend('PATCH', `/api/licensing/instructions/${id}`, payload);
    return response.data;
  },

  async deleteLicensingInstruction(id: string) {
    const response = await jsonSend('DELETE', `/api/licensing/instructions/${id}`, undefined);
    return response.data;
  },

  async resetLicensingInstruction(id: string) {
    const response = await jsonSend('POST', `/api/licensing/instructions/${id}/reset`, undefined);
    return response.data;
  },

  async reorderLicensingInstructions(orderedIds: string[]) {
    const response = await jsonSend('POST', '/api/licensing/instructions/reorder', { orderedIds });
    return response.data;
  },

  async runLicensingAnalysis(
    application_ids?: string[] | null,
    instructions?: string | null,
    exclude_document_app_ids?: string[] | null,
    assumption_set?: any,
    grouping?: 'vendor' | 'parent' | null,
    document_ids?: string[] | null,
  ) {
    const response = await jsonSend('POST', '/api/licensing/run', {
      application_ids: application_ids || undefined,
      instructions: instructions || undefined,
      exclude_document_app_ids:
        exclude_document_app_ids && exclude_document_app_ids.length
          ? exclude_document_app_ids
          : undefined,
      assumption_set,
      // 'vendor' is the server default; only send an explicit override.
      grouping: grouping === 'parent' ? 'parent' : undefined,
      // Documents chosen from the central Licensing Knowledge Library. Omitted (not []) when
      // nothing is ticked, so the server keeps its legacy per-application lookup.
      document_ids: document_ids && document_ids.length ? document_ids : undefined,
    });
    return response.data;
  },

  async getLicensingRunStatus(runId: string) {
    const response = await jsonGet(`/api/licensing/run/${runId}/status`);
    return response.data;
  },

  async getLicensingResults(applicationId?: string) {
    const response = await jsonGet(
      '/api/licensing/results',
      applicationId ? { applicationId } : {},
    );
    return response.data;
  },

  async getLicensingRuns() {
    const response = await jsonGet('/api/licensing/runs');
    return response.data;
  },

  async deleteLicensingRun(runId: string) {
    const response = await jsonSend('DELETE', `/api/licensing/run/${runId}`, undefined);
    return response.data;
  },

  async deleteLicensingAnalysisRecord(runId: string, applicationId: string) {
    const response = await jsonSend(
      'DELETE',
      `/api/licensing/run/${runId}/app/${applicationId}`,
      undefined,
    );
    return response.data;
  },

  async getLicensingAnalysis(
    params: { run_id?: string; q?: string; field?: string; disposition?: string } = {},
  ) {
    const response = await jsonGet('/api/licensing/analysis', params);
    return response.data;
  },

  async getLicensingDocuments() {
    const response = await jsonGet('/api/licensing/documents');
    return response.data;
  },

  async downloadLicensingWord(
    runId: string,
    applicationId?: string,
    fileName?: string,
    familyId?: string,
    /** A VENDOR roll-up — every analysed application from that vendor. */
    vendorFamily?: string,
  ) {
    const response = await blobGet(
      `/api/licensing/run/${runId}/word`,
      vendorFamily ? { vendorFamily }
        : familyId ? { familyId }
        : applicationId ? { applicationId }
        : undefined,
    );
    const cd = (response.headers && (response.headers as any)['content-disposition']) || '';
    const m = /filename="?([^";]+)"?/.exec(cd);
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || (m && m[1]) || 'Licensing_Analysis.docx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  async downloadLicensingDeck(
    runId: string,
    applicationId?: string,
    fileName?: string,
    familyId?: string,
    /** A VENDOR roll-up — every analysed application from that vendor. */
    vendorFamily?: string,
  ) {
    const response = await blobGet(
      `/api/licensing/run/${runId}/deck`,
      vendorFamily ? { vendorFamily }
        : familyId ? { familyId }
        : applicationId ? { applicationId }
        : undefined,
    );
    const cd = (response.headers && (response.headers as any)['content-disposition']) || '';
    const m = /filename="?([^";]+)"?/.exec(cd);
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || (m && m[1]) || 'Licensing_Analysis.pptx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  async generateLicensingModule(payload: {
    vendor?: string;
    lifecycle_stage?: string;
    commercial_model?: string;
    notes?: string;
    application_ids?: string[];
    replace?: boolean;
  }) {
    const response = await jsonSend('POST', '/api/licensing/instructions/generate', payload);
    return response.data;
  },

  async getLicensingModuleJob(jobId: string) {
    const response = await jsonGet(`/api/licensing/instructions/generate/${jobId}`);
    return response.data;
  },

  async deleteScoringThreshold(id: string) {
    return authFetch(`/api/scoring-thresholds/${id}`, { method: 'DELETE' }).then((r) => r.json());
  },

  post: (url: string, body?: unknown) =>
    authFetch(url, { method: 'POST', body: JSON.stringify(body ?? {}) }).then((r) => r.json()),
  put: (url: string, body?: unknown) =>
    authFetch(url, { method: 'PUT', body: JSON.stringify(body ?? {}) }).then((r) => r.json()),
  delete: (url: string) => authFetch(url, { method: 'DELETE' }).then((r) => r.json()),

  // ─── Application Evaluation ──────────────────────────────────────────────────
  //
  // Ported VERBATIM from the source's `apiService` — same URLs, same params, same
  // snake_case-to-camelCase transforms, same cache-busting `_t` stamps. The source
  // declares these as class methods; here they are members of an object literal, so
  // each gains a trailing comma. That is the ONLY edit: they still read `this.api`,
  // which is the axios-shaped client (./aprAxios.ts), so no body was rewritten around
  // a different transport.

  // Frameworks
  async getFrameworks() {
    const response = await this.api.get('/frameworks');
    return response.data;
  },

  async getActiveFrameworks() {
    const response = await this.api.get('/frameworks');
    // Filter to only return active frameworks
    return response.data.filter((fw: any) => fw.status === 'Active');
  },

  async getDimensions() {
    const response = await this.api.get('/dimensions');
    return response.data;
  },

  // Customers
  async getCustomers() {
    const response = await this.api.get('/customers');
    return response.data;
  },

  // Models
  async getModels() {
    console.log('🔄 API Service: Calling /models endpoint...');
    const response = await this.api.get(`/models?_t=${Date.now()}`);
    console.log('📥 API Service: Raw models response:', response.data);
    // Transform snake_case API response to camelCase for TypeScript interfaces
    const transformedModels = response.data.map((model: any) => ({
      id: model.id,
      name: model.name,
      description: model.description,
      status: model.status,
      frameworkId: model.framework_id,
      frameworkName: model.framework_name,
      dimensions: model.dimensions || [],
      applicationCount: model.application_count || 0,
      completedApplications: model.completed_applications || 0,
      runs: [] // APR runs will be loaded separately if needed
    }));
    console.log('🔄 API Service: Transformed models:', transformedModels);
    return transformedModels;
  },

  async createModel(modelData: any) {
    const payload = {
      name: modelData.name,
      description: modelData.description,
      status: modelData.status || 'Created',
      framework_id: modelData.frameworkId,
    };
    const response = await this.api.post('/models', payload);
    return response.data;
  },

  async updateModel(id: string, modelData: any) {
    const payload = {
      name: modelData.name,
      description: modelData.description,
      status: modelData.status,
      framework_id: modelData.frameworkId,
    };
    const response = await this.api.put(`/models/${id}`, payload);
    return response.data;
  },

  async deleteModel(id: string) {
    const response = await this.api.delete(`/models/${id}`);
    return response.data;
  },

  async getModelApplications(modelId: string) {
    const response = await this.api.get(`/models/${modelId}/applications`);
    // Transform snake_case API response to camelCase for TypeScript interfaces
    return response.data.map((app: any) => ({
      id: app.id,
      name: app.name,
      description: app.description,
      customerId: app.customer_id,
      aprModelId: app.apr_model_id,
      costs: [], // Costs will be loaded separately if needed
      responses: [], // Responses will be loaded separately if needed
      recommendation: app.recommendation,
      overallScore: app.overall_score,
      // Application Master Data fields
      applicationIdCode: app.application_id_code,
      // Lifecycle progress — derived server-side per stage, never stored as flags.
      qaAnsweredCount: app.qa_answered_count,
      qaTotalQuestions: app.qa_total_questions,
      hasInitialDisposition: app.has_initial_disposition,
      // Kept apart because they carry different authority: a Q&A-driven disposition is
      // the framework's own answer, an AI one is advisory input the client may ignore.
      manualInitialDisposition: app.manual_initial_disposition,
      aiInitialDisposition: app.ai_initial_disposition,
      hasAiAnalysis: app.has_ai_analysis,
      hasFinalDisposition: app.has_final_disposition,
      hasIntegrationGroup: app.has_integration_group,
      hasRoadmap: app.has_roadmap,
      deploymentStatus: app.deployment_status,
      itHardwareInfrastructure: app.it_hardware_infrastructure,
      dateOfIntroduction: app.date_of_introduction,
      contractEndDate: app.contract_end_date,
      applicationOwner: app.application_owner,
      appOwnerEmail: app.app_owner_email,
      bpoEmail: app.bpo_email,
      securityOwnerEmail: app.security_owner_email,
      executiveOwnerEmail: app.executive_owner_email,
      technicalOwnerCustodian: app.technical_owner_custodian,
      toEmail: app.to_email,
      businessFunctionDomain: app.business_function_domain,
      applicationType: app.application_type,
      technologyStack: app.technology_stack,
      currentOperating: app.current_operating,
      userCount: app.user_count,
      businessCriticality: app.business_criticality,
      technicalHealthStatus: app.technical_health_status,
      integrationDependencies: app.integration_dependencies,
      vendorSupplier: app.vendor_supplier,
      // Parent application flag
      isParent: app.is_parent || false
    }));
  },

  // APR Model Applications
  async updateModelApplications(modelId: string, applicationIds: string[]) {
    const response = await this.api.post(`/models/${modelId}/applications`, { applicationIds });
    return response.data;
  },

  async getModelApplicationStats(): Promise<{ assigned: number; unassigned: number }> {
    const response = await this.api.get('/models/application-stats');
    return response.data;
  },

  /**
   * Create any missing owner accounts and send the invitation in one call.
   *
   * Replaces getModelOwners(id, true) + the operator's own mail client. The body is
   * passed because the screen lets it be edited; everything else the server resolves.
   */
  async sendModelInvitations(modelId: string, body: string, subject?: string) {
    const response = await jsonSend('POST', `/api/models/${modelId}/send-invitations`, {
      body,
      ...(subject ? { subject } : {}),
    });
    return response.data;
  },

  async getModelOwners(modelId: string, createAccounts: boolean = false) {
    const response = await this.api.post(`/models/${modelId}/invite-owners`, { createAccounts });
    return response.data;
  },

  async getAssignedApplicationIds(): Promise<string[]> {
    const response = await this.api.get('/models/assigned-application-ids');
    return response.data;
  },

  async searchModelsByApplication(query: string): Promise<string[]> {
    const response = await this.api.get('/models/search-by-application', { params: { q: query } });
    return response.data;
  },

  // APR Runs
  async getAPRRuns() {
    const response = await this.api.get('/runs');
    return response.data;
  },

  // Get all current run rate data
  async getCurrentRunRateData() {
    console.log('🔄 API Service: Calling /current-run-rate endpoint...');
    const response = await this.api.get(`/current-run-rate?_t=${Date.now()}`);
    // Transform snake_case API response to camelCase for TypeScript interfaces
    return response.data.map((item: any) => ({
      id: item.id,
      applicationId: item.application_id,
      applicationName: item.application_name,
      totalAnnualCost: parseFloat(item.total_annual_cost) || 0,
      annualSoftwareLicensingFees: parseFloat(item.annual_software_licensing_fees) || 0,
      annualThirdPartyVendorSupport: parseFloat(item.annual_third_party_vendor_support) || 0,
      annualInfrastructureCost: parseFloat(item.annual_infrastructure_cost) || 0,
      indirectOperationalCosts: parseFloat(item.indirect_operational_costs) || 0,
      cloudSaasSubscriptionCostImpact: parseFloat(item.cloud_saas_subscription_cost_impact) || 0,
      // Audit fields
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      createdBy: item.created_by,
      updatedBy: item.updated_by,
      createdByName: item.created_by_name,
      updatedByName: item.updated_by_name,
      // Parent-child fields
      isParent: item.is_parent || false,
      isParentSummary: item.is_parent_summary || false,
      childCostCount: item.child_cost_count || 0,
      parentApplicationId: item.parent_application_id || null,
      parentApplicationName: item.parent_application_name || null,
      parentApplicationCode: item.parent_application_code || null,
      tcoStatus: item.tco_status || null,
    }));
  },

  async getScoringPortfolioData(statusFilter?: string) {
    const params = statusFilter ? `?status_filter=${statusFilter}` : '';
    const response = await this.api.get(`/assessments/scoring-portfolio${params}`);
    return response.data;
  },

  // Scoring audit trail: every Q&A answer change (old→new answer + score, who, when).
  async getScoringAudit(params?: { apr_run_id?: string; application_id?: string }) {
    const qs = new URLSearchParams();
    if (params?.apr_run_id) qs.set('apr_run_id', params.apr_run_id);
    if (params?.application_id) qs.set('application_id', params.application_id);
    const q = qs.toString();
    const response = await this.api.get(`/assessments/scoring-audit${q ? '?' + q : ''}`);
    return response.data;
  },

  async rescoreApplication(applicationId: string, aprRunId?: string) {
    const response = await this.api.post('/assessments/rescore-application', {
      application_id: applicationId,
      apr_run_id: aprRunId || null
    });
    return response.data;
  },

  async getQAStatuses(): Promise<
    {
      application_id: string;
      total_questions: number;
      answered_questions: number;
      /** One entry per responsible aspect present on the questionnaire — who owes what. */
      aspects?: { aspect: string; total_questions: number; answered_questions: number }[];
    }[]
  > {
    const response = await this.api.get('/assessments/qa-statuses');
    return response.data;
  },

  async initializeQAAssessments(applications: any[], aprRunId?: string, aprModelId?: string) {
    const response = await this.api.post('/assessments/initialize-qa', {
      applications,
      apr_run_id: aprRunId,
      apr_model_id: aprModelId
    });
    return response.data;
  },

  async getApplicationQuestions(applicationId: string, aprRunId?: string) {
    const params = new URLSearchParams();
    params.append('application_id', applicationId);
    if (aprRunId) {
      params.append('apr_run_id', aprRunId);
    }
    
    const response = await this.api.get(`/assessments/questions?${params.toString()}`);
    return response.data;
  },

  async saveAssessmentResponse(data: {
    application_id: string;
    question_id: string;
    answer_option_id: string;
    apr_run_id?: string;
    score?: number;
    weight?: number;
  }) {
    const response = await this.api.post('/assessments/qa-answer', data);
    return response.data;
  },

  async submitBulkAssessmentResponses(applicationId: string, responses: any[], aprRunId?: string) {
    const response = await this.api.post('/assessments/bulk', {
      application_id: applicationId,
      apr_run_id: aprRunId,
      responses
    });
    return response.data;
  },

  // Alias for backward compatibility with QuestionsAnswersModal
  async submitBulkAssessment(applicationId: string, aprRunId: string | undefined, responses: any[]) {
    return this.submitBulkAssessmentResponses(applicationId, responses, aprRunId);
  },
};

export default apiService;
